import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { StorageContext } from '../contexts/StorageContext';
import { AuthContext } from '../contexts/AuthContext';
import { createNewProject, importProject } from '../utils/projectUtils';
import { getRecentProjects, addRecentProject } from '../utils/configUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import './HomePage.css';

/**
 * Home page component
 * Entry point of the application with project creation and listing
 */
function HomePage() {
  const { storage } = useContext(StorageContext);
  const { isAuthenticated, isPremium } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [projectTemplate, setProjectTemplate] = useState('blank');
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all projects from storage
        const allProjects = await storage.getAllProjects();
        setProjects(allProjects);
        
        // Get recent projects from config
        const recent = getRecentProjects();
        setRecentProjects(recent);
      } catch (err) {
        console.error('Error loading projects:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    
    loadProjects();
  }, [storage]);
  
  // Handle new project form toggle
  const toggleNewProject = () => {
    setShowNewProject(prev => !prev);
    setNewProjectName('');
    setNewProjectDesc('');
    setProjectTemplate('blank');
  };
  
  // Handle project name change
  const handleNameChange = (e) => {
    setNewProjectName(e.target.value);
  };
  
  // Handle project description change
  const handleDescChange = (e) => {
    setNewProjectDesc(e.target.value);
  };
  
  // Handle template selection
  const handleTemplateChange = (e) => {
    setProjectTemplate(e.target.value);
  };
  
  // Handle create project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    if (!newProjectName.trim()) {
      setError('Project name is required');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create new project
      const projectData = {
        name: newProjectName,
        description: newProjectDesc,
        template: projectTemplate
      };
      
      const projectId = await createNewProject(storage, projectData);
      
      // Add to recent projects
      addRecentProject({
        id: projectId,
        name: newProjectName,
        updated: Date.now()
      });
      
      // Navigate to new project
      navigate(`/project/${projectId}`);
    } catch (err) {
      console.error('Error creating project:', err);
      setError('Failed to create project');
      setLoading(false);
    }
  };
  
  // Handle open project
  const handleOpenProject = (projectId) => {
    navigate(`/project/${projectId}`);
  };
  
  // Handle import project button click
  const handleImportClick = () => {
    fileInputRef.current.click();
  };
  
  // Handle file selection for import
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Import project from file
      const projectId = await importProject(storage, file);
      
      // Get project data
      const project = await storage.getProject(projectId);
      
      // Add to recent projects
      addRecentProject({
        id: projectId,
        name: project.name,
        updated: Date.now()
      });
      
      // Navigate to imported project
      navigate(`/project/${projectId}`);
    } catch (err) {
      console.error('Error importing project:', err);
      setError('Failed to import project. The file may be corrupted or in an invalid format.');
      setLoading(false);
    }
  };
  
  // Get project template options
  const templates = [
    { id: 'blank', name: 'Blank Project', description: 'Start from scratch' },
    { id: 'web', name: 'Web Application', description: 'React-based web app' },
    { id: 'api', name: 'REST API', description: 'Node.js API server' },
    { id: 'mobile', name: 'Mobile App', description: 'React Native mobile app' },
    { id: 'desktop', name: 'Desktop App', description: 'Electron desktop app' },
    { id: 'cli', name: 'CLI Tool', description: 'Command-line interface tool' }
  ];
  
  // Render loading state
  if (loading && !showNewProject) {
    return (
      <div className="home-loading">
        <LoadingSpinner message="Loading projects..." />
      </div>
    );
  }
  
  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-content">
          <h1>AI-Assisted Code Implementation</h1>
          <p className="hero-description">
            Efficiently implement software projects using AI assistants while keeping all your code private and secure.
          </p>
          <div className="hero-actions">
            <button className="create-button" onClick={toggleNewProject}>
              Create New Project
            </button>
            <button className="import-button" onClick={handleImportClick}>
              Import Project
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileSelect} 
              style={{ display: 'none' }} 
              accept=".json"
            />
          </div>
        </div>
      </section>
      
      {showNewProject && (
        <section className="new-project-section">
          <div className="new-project-form">
            <h2>Create New Project</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleCreateProject}>
              <div className="form-group">
                <label htmlFor="project-name">Project Name</label>
                <input 
                  type="text" 
                  id="project-name" 
                  value={newProjectName} 
                  onChange={handleNameChange} 
                  placeholder="Enter project name" 
                  required 
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="project-description">Description (Optional)</label>
                <textarea 
                  id="project-description" 
                  value={newProjectDesc} 
                  onChange={handleDescChange} 
                  placeholder="Brief description of your project" 
                  rows="3" 
                />
              </div>
              
              <div className="form-group">
                <label>Project Template</label>
                <div className="template-options">
                  {templates.map(template => (
                    <div 
                      key={template.id} 
                      className={`template-option ${projectTemplate === template.id ? 'selected' : ''}`}
                      onClick={() => setProjectTemplate(template.id)}
                    >
                      <div className="template-header">
                        <input 
                          type="radio" 
                          name="template" 
                          value={template.id} 
                          checked={projectTemplate === template.id} 
                          onChange={handleTemplateChange} 
                        />
                        <span className="template-name">{template.name}</span>
                      </div>
                      <p className="template-description">{template.description}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={toggleNewProject}>
                  Cancel
                </button>
                <button type="submit" className="submit-button" disabled={loading || !newProjectName.trim()}>
                  {loading ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
      
      <section className="projects-section">
        <h2>Your Projects</h2>
        
        {error && !showNewProject && (
          <div className="error-message">{error}</div>
        )}
        
        {projects.length > 0 ? (
          <div className="projects-grid">
            {projects.map(project => (
              <div 
                key={project.id} 
                className="project-card"
                onClick={() => handleOpenProject(project.id)}
              >
                <div className="project-header">
                  <h3 className="project-name">{project.name}</h3>
                  {project.completed && (
                    <span className="completed-badge">Completed</span>
                  )}
                </div>
                <p className="project-description">
                  {project.description || 'No description provided'}
                </p>
                <div className="project-meta">
                  <span className="project-date">
                    Updated: {new Date(project.updated).toLocaleDateString()}
                  </span>
                  <span className="project-phase">
                    Phase: {project.currentPhase || 'PLANNING'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-projects">
            <p>You don't have any projects yet. Create a new project to get started!</p>
          </div>
        )}
      </section>
      
      {recentProjects.length > 0 && (
        <section className="recent-section">
          <h2>Recently Viewed</h2>
          <div className="recent-projects">
            {recentProjects.map(recent => {
              const project = projects.find(p => p.id === recent.id);
              return project ? (
                <div 
                  key={recent.id} 
                  className="recent-project-item"
                  onClick={() => handleOpenProject(recent.id)}
                >
                  <span className="recent-project-name">{project.name}</span>
                  <span className="recent-project-date">
                    {new Date(project.updated).toLocaleDateString()}
                  </span>
                </div>
              ) : null;
            })}
          </div>
        </section>
      )}
      
      <section className="features-section">
        <h2>Key Features</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3>100% Private</h3>
            <p>All project data stays in your browser. Nothing is sent to our servers.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🤖</div>
            <h3>AI Optimized</h3>
            <p>Structured prompts maximize the effectiveness of AI coding assistants.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📋</div>
            <h3>Phased Approach</h3>
            <p>Follow a structured development process from planning to completion.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">📱</div>
            <h3>Multi-Platform</h3>
            <p>Create web, mobile, desktop, and backend applications with ease.</p>
          </div>
        </div>
      </section>
      
      {!isAuthenticated && (
        <section className="cta-section">
          <div className="cta-content">
            <h2>Unlock Premium Features</h2>
            <p>
              Upgrade to Premium for unlimited projects, advanced templates, and an ad-free experience.
            </p>
            <button className="cta-button">Upgrade to Premium</button>
          </div>
        </section>
      )}
    </div>
  );
}

export default HomePage;
